import { Link } from "react-router-dom";
import type { TaskRunCollection } from "../../../../shared/types";

/** Only persisted, authorized collection evidence is projected into this contract. */
export function SourceCollectionReport({ collection, brandId, datasetContext = false }: { collection: TaskRunCollection; brandId: string; datasetContext?: boolean }) {
  const counts = collection.status_counts;
  if (collection.dataset) return <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" aria-label="Saved dataset result">
    <h2 className="text-lg font-semibold">Saved dataset</h2>
    <p>{collection.dataset.row_count} rows · Query version {collection.dataset.query_version} · {collection.dataset.completeness}</p>
    <p>{collection.state === "partial" ? "Partial data is readable. Inspect coverage before using it." : "The saved dataset is readable and verified."}</p>
    <Link className="text-brand-700 underline" to={collection.dataset.route_path}>Inspect saved dataset</Link>
  </section>;
  if (datasetContext) return <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" aria-label="Saved dataset result">
    <h2 className="text-lg font-semibold">Saved dataset</h2>
    <p>Saved dataset evidence could not be verified in this view.</p>
    <Link className="text-brand-700 underline" to={collection.source_path}>Inspect dataset Action</Link>
  </section>;
  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" aria-label="Source collection report">
      <h2 className="text-lg font-semibold">Source collection</h2>
      <p>{collection.state === "collected"
        ? "The collection evidence is saved and verified."
        : collection.state === "partial"
          ? collection.artifacts.length === 0 && counts?.failed && !counts.succeeded && !counts.partial
            ? "Collection failed; no saved artifact was verified. Inspect the failed Run and source connection."
            : collection.artifacts.length === 0
              ? "Collection needs attention; no saved artifact was verified. Inspect the source Runs."
              : "Some source reports were collected. Other reports need attention."
          : "The saved evidence could not be verified. Check the source connection and Run details."}</p>
      {counts && <p>{counts.succeeded} succeeded · {counts.partial} partial · {counts.failed} failed</p>}
      {collection.children.length > 0 && <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr><th>Source Run</th><th>Result</th><th>Provider rows</th><th>Requested dates</th></tr></thead>
          <tbody>{collection.children.map((child) => <tr className="border-t" key={child.run_id}>
            <td className="py-2 pr-3"><Link className="text-brand-700 underline" to={`/runs/${encodeURIComponent(child.run_id)}?brandId=${encodeURIComponent(brandId)}`}>Open Run {child.run_id.slice(0, 8)}</Link></td>
            <td className="py-2 pr-3">{child.status}</td>
            <td className="py-2 pr-3">{child.rows ?? "unknown"}</td>
            <td className="py-2">{child.requested_start ?? "unknown"} – {child.requested_end ?? "unknown"}</td>
          </tr>)}</tbody>
        </table>
      </div>}
      <p>{collection.artifacts.length} verified saved artifact{collection.artifacts.length === 1 ? "" : "s"}</p>
      {collection.artifacts.length > 0 && <ul className="space-y-1">{collection.artifacts.map((artifact) => (
        <li key={artifact.output_id}><Link className="text-brand-700 underline" to={artifact.route_path}>View collection evidence {artifact.artifact_id.slice(0, 8)}</Link></li>
      ))}</ul>}
      <Link className="inline-flex min-h-11 items-center text-brand-700 underline" to={collection.source_path}>Inspect source connections</Link>
    </section>
  );
}
